import GlassCard from '@/components/ui/glass-card';
import { type SimplifiedAppointment } from '@/types/appointment';
import { Calendar, Clock, Stethoscope, User } from 'lucide-react';

/**
 * Formats an ISO date string to MM/dd/yyyy format using UTC methods to preserve the exact date
 */
const formatDateOfService = (dateString: string): string => {
  try {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string provided:', dateString);
      return dateString; // Return original if invalid
    }

    // Use UTC methods to preserve the exact date as received from API
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date of service:', error);
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Formats an ISO date string to hh:mm a format (12-hour format with AM/PM)
 */
const formatAppointmentTime = (dateString: string): string => {
  try {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string provided:', dateString);
      return dateString; // Return original if invalid
    }

    // Use local time for appointment time display
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Convert to 12-hour format
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedMinutes = String(minutes).padStart(2, '0');

    return `${hour12}:${formattedMinutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting appointment time:', error);
    return dateString; // Return original string if parsing fails
  }
};

interface PatientInfoBarProps {
  selectedAppointment: SimplifiedAppointment | null;
  className?: string;
}

const PatientInfoBar = ({
  selectedAppointment,
  className = '',
}: PatientInfoBarProps) => {
  if (!selectedAppointment) {
    return null;
  }

  const patientName = `${selectedAppointment.patientFirstName || ''} ${
    selectedAppointment.patientLastName || ''
  }`.trim();
  const service = selectedAppointment.service || 'N/A';
  const dateOfService = formatDateOfService(
    selectedAppointment.startTime || ''
  );
  const appointmentTime = formatAppointmentTime(
    selectedAppointment.startTime || ''
  );

  return (
    <GlassCard className={`${className}`} variant="subtle">
      <div className="p-2 md:p-2">
        {/* Mobile Layout - Labels on left, values on right */}
        <div className="flex flex-col gap-3 md:hidden">
          {/* Patient Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-purple-500 flex-shrink-0" />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Patient
              </span>
            </div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate text-right">
              {patientName || 'Unknown Patient'}
            </span>
          </div>

          {/* Service Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Service
              </span>
            </div>
            <span className="text-sm text-slate-600 dark:text-slate-300 truncate text-right">
              {service}
            </span>
          </div>

          {/* Date Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Date
              </span>
            </div>
            <span className="text-sm text-slate-600 dark:text-slate-300 text-right">
              {dateOfService}
            </span>
          </div>

          {/* Time Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Time
              </span>
            </div>
            <span className="text-sm text-slate-600 dark:text-slate-300 text-right">
              {appointmentTime}
            </span>
          </div>
        </div>

        {/* iPad Layout - Compact layout matching audio controls height */}
        <div className="hidden md:flex md:items-center md:h-full">
          {/* All fields with optimized column widths - Patient and Service get more space */}
          <div
            className="grid gap-2 w-full"
            style={{ gridTemplateColumns: '1.4fr 2fr 1.2fr 1.2fr' }}>
            <div className="flex flex-col justify-center min-h-0">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight flex items-center gap-1">
                <User className="w-4 h-4 text-purple-500 flex-shrink-0" />
                Patient
              </span>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate leading-tight">
                {patientName || 'Unknown Patient'}
              </span>
            </div>

            <div className="flex flex-col justify-center min-h-0">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight flex items-center gap-1">
                <Stethoscope className="w-4 h-4 text-blue-500 flex-shrink-0" />
                Service
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-300 truncate leading-tight">
                {service}
              </span>
            </div>

            <div className="flex flex-col justify-center min-h-0">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight flex items-center gap-1">
                <Calendar className="w-4 h-4 text-green-500 flex-shrink-0" />
                Date
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-300 leading-tight">
                {dateOfService}
              </span>
            </div>

            <div className="flex flex-col justify-center min-h-0">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight flex items-center gap-1">
                <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
                Time
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-300 leading-tight">
                {appointmentTime}
              </span>
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

export default PatientInfoBar;
